<template>
  <div class="template-info">
    <div class="template-info__base">
      <h1 class="template-info__title">
        <span class="template-info__title-text">
          {{ templateName }}
        </span>
      </h1>
      <iconpark-icon
        v-if="isHot && !isNew"
        name="fire"
        size="18"
        class="template-info__hot-icon"
      ></iconpark-icon>
      <span v-if="isNew" class="template-info__new-badge">
        <iconpark-icon name="new" class="template-info__new-icon" size="30"></iconpark-icon>
      </span>
    </div>

    <div class="template-info__description-container">
      <!-- 描述 -->
      <div v-if="description" class="template-info__description" v-html="description" />

      <!-- 标签 -->
      <TagList :new_tag_ids="newTagIds" v-if="newTagIds" :max-length="6" />

      <!-- 作者 -->
      <div class="template-info__meta">
        <span
          class="template-info__author"
          :class="{ 'template-info__author--clickable': true }"
          :title="avatarName"
          @click.stop="emit('avatarClick')"
        >
          <img loading="lazy" class="template-info__avatar" :src="avatarUrl" />
          {{ avatarName }}
        </span>
        &nbsp;&nbsp;|&nbsp;&nbsp;
        <div class="template-info__online-time">
          {{ publishTime }}
        </div>
      </div>
    </div>

    <!-- 操作按钮区域 -->
    <div class="template-info__actions">
      <BaseButton
        variant="black-large"
        class="template-info__actions-create-button"
        :disabled="copyrightLimited"
        @click="emit('useTemplate')"
      >
        {{ copyrightLimited ? $t('zhi-du-mo-ban') : $t('ai-chuang-zuo-tong-kuan-shi-pin') }}
        <template #icon>
          <IconMoreRight class="template-info__button-icon-go" />
        </template>
      </BaseButton>

      <Tooltip placement="top">
        <template #content>
          {{ isStarPersonal ? $t('qu-xiao-shou-cang') : $t('shou-cang') }}
        </template>
        <BaseButton variant="circle-border-white-large" @click="emit('collectChange')">
          <template #icon>
            <IconLikeFill v-if="isStarPersonal" class="template-info__button-icon" />
            <IconLike v-else class="template-info__button-icon" />
          </template>
        </BaseButton>
      </Tooltip>

      <a-popover
        trigger="click"
        overlay-class-name="ss-popover-custom"
        :open="open.popover"
        placement="topRight"
        @open-change="popoverOpenChange"
      >
        <template #content>
          <div class="template-info__share-popover">
            <div class="template-info__share-title">
              {{ $t('fen-xiang-mo-ban') }}
            </div>
            <BaseButton
              variant="black-middle"
              class="template-info__share-copy-button"
              @click="handleCopy"
            >
              {{ $t('fu-zhi-lian-jie') }}
              <template #icon>
                <IconShareLink class="template-info__button-icon" />
              </template>
            </BaseButton>
          </div>
        </template>
        <Tooltip :open="open.tooltip" @open-change="tooltipOpenChange">
          <template #content>
            {{ $t('fen-xiang') }}
          </template>
          <BaseButton
            variant="circle-border-white-large"
            :class="{ 'template-info__share-button--active': open.popover }"
          >
            <template #icon>
              <IconShare class="template-info__button-icon" />
            </template>
          </BaseButton>
        </Tooltip>
      </a-popover>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { getTemplateShareText } from '@/api/module/template';
import Tooltip from '@rendora/design/components/tooltip/Tooltip.vue';
import { useI18n } from 'vue-i18n';
import type { NewTagNode } from '~/api/interface';
import IconShareLink from '~/assets/svg/share-link.svg?component';
import BaseButton from '~/design/components/buttons/base.vue';
import IconLikeFill from '~/design/icons/favorite-filled.svg';
import IconLike from '~/design/icons/favorite.svg';
import IconMoreRight from '~/design/icons/more-right.svg?component';
import IconShare from '~/design/icons/share.svg?component';
import { copyToClipboard } from '~/utils';
import TagList from './TagList.vue';

const props = defineProps<{
  templateName?: string;
  isHot?: boolean;
  isNew?: boolean;
  description?: string;
  newTagIds?: Record<number, NewTagNode>;
  avatarUrl?: string;
  avatarName?: string;
  publishTime?: string;
  copyrightLimited?: boolean;
  isStarPersonal?: boolean;
  templateId?: number;
}>();

const emit = defineEmits<{
  avatarClick: [];
  useTemplate: [];
  collectChange: [];
  share: [];
}>();

const { t: $t } = useI18n();

// 处理分享按钮的 popover + tooltip 互斥
const open = reactive({
  popover: false,
  tooltip: false,
});

const location = computed(() => {
  if (import.meta.client) {
    return window.location;
  }
  return null;
});

function popoverOpenChange(visible: boolean) {
  open.tooltip = false;
  open.popover = visible;
}

function tooltipOpenChange(visible: boolean) {
  if (open.popover) {
    return;
  }
  open.tooltip = visible;
}

function handleCopy() {
  getTemplateShareText({ template_id: props.templateId }).then(res => {
    const [start, end] = res.data;
    const msg =
      start && end
        ? `${start || ''} ${location.value?.origin}/template/${props.templateId}.html ${end || ''}`
        : '';
    copyToClipboard(msg);
    message.success($t('fu-zhi-cheng-gong'));
    open.popover = false;
  });
}
</script>

<style scoped lang="scss">
.template-info__button-icon-go {
  height: 22px;
  width: 22px;
}
.template-info {
  width: 384px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  justify-content: space-between;
}

.template-info__description-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

// 标题
.template-info__title {
  display: flex;
  margin: 0;
  font-weight: var(--x-weight-regular);
  color: var(--x-text-1);
  font-size: var(--x-text-24-size);
  line-height: var(--x-text-24-line-height);
}

// 标题文本
.template-info__title-text {
  margin-right: 8px;
}

// 热门图标
.template-info__hot-icon {
  color: var(--x-error-6);
  margin-left: 3px;
}

// 新标签徽章
.template-info__new-badge {
  width: 44px;
  height: 20px;
  margin-left: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--primary-color);
  border-radius: 6px;
}

// 新标签图标
.template-info__new-icon {
  color: white;
}

// 元信息区域
.template-info__meta {
  display: flex;
  align-items: center;
  color: var(--x-text-8);
  font-size: 14px;

  .divider {
    width: 1px;
    height: 16px;
    background-color: var(--x-back-4);
  }
}

// 作者信息
.template-info__author {
  display: flex;
  align-items: center;
  gap: 4px;
}

// 作者可点击状态
.template-info__author--clickable {
  cursor: pointer;
}

.template-info__author--clickable:hover {
  color: var(--x-text-7);
}

// 头像
.template-info__avatar {
  width: 18px;
  height: 18px;
  border-radius: 50%;
}

// 描述区域
.template-info__description {
  font-size: var(--x-text-16-size);
  color: var(--x-text-5);
  line-height: var(--x-text-20-line-height);
  position: relative;
  white-space: pre-wrap;
  word-break: break-word;
  &:deep(p) {
    margin: 0;
  }
}

// 操作按钮区域
.template-info__actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

// 按钮图标
.template-info__button-icon {
  height: 24px;
  width: 24px;
}

// 创建按钮
.template-info__actions-create-button {
  width: 236px;
}

// 分享弹窗内容
.template-info__share-popover {
  padding: 12px;
  text-align: center;
}

// 分享标题
.template-info__share-title {
  padding-bottom: 24px;
  font-size: 20px;
  color: var(--x-text-1);
  line-height: 28px;
}

// 复制按钮
.template-info__share-copy-button {
  width: 320px;
}
</style>
